import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { NAV_LINKS } from '@/lib/site'
import { SearchDialog, useSearchHotkey } from '@/components/search/SearchDialog'

function Wordmark() {
  return (
    <Link to="/" className="group flex items-center gap-2.5" aria-label="MitoHacker home">
      <img
        src="/brand/mark-white.svg"
        alt=""
        width={30}
        height={30}
        className="h-[30px] w-[30px] transition-transform duration-500 ease-out group-hover:rotate-[8deg]"
      />
      <span className="font-display text-[19px] font-semibold tracking-tight">
        <span className="text-mist">MITO</span>
        <span className="text-fog">HACKER</span>
      </span>
    </Link>
  )
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const openSearch = useCallback(() => setSearchOpen(true), [])
  useSearchHotkey(openSearch)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled ? 'border-b border-line/70 bg-void/80 backdrop-blur-xl' : 'border-b border-transparent'
      }`}
    >
      <nav className="container-page flex h-16 items-center justify-between">
        <Wordmark />

        {/* Desktop links */}
        <ul className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <NavLink
                to={l.href}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${
                    isActive ? 'text-energy' : 'text-fog hover:text-mist'
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={openSearch}
              aria-label="Search articles"
              className="ml-1 flex items-center gap-2 rounded-full px-3 py-2 text-[15px] font-medium text-fog transition-colors hover:text-mist"
            >
              <span aria-hidden>⌕</span>
              <span className="hidden lg:inline text-sm text-faint">⌘K</span>
            </button>
          </li>
          <li>
            <Link
              to="/learn"
              className="ml-1 rounded-full border border-line-strong px-4 py-2 text-[15px] font-medium text-mist transition-colors hover:border-energy hover:text-energy"
            >
              Start Here
            </Link>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button
          type="button"
          className="grid h-10 w-10 place-items-center md:hidden"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="relative block h-4 w-6">
            <span
              className={`absolute left-0 h-0.5 w-6 bg-mist transition-all duration-300 ${open ? 'top-2 rotate-45' : 'top-0'}`}
            />
            <span
              className={`absolute left-0 top-2 h-0.5 w-6 bg-mist transition-all duration-200 ${open ? 'opacity-0' : 'opacity-100'}`}
            />
            <span
              className={`absolute left-0 h-0.5 w-6 bg-mist transition-all duration-300 ${open ? 'top-2 -rotate-45' : 'top-4'}`}
            />
          </span>
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed inset-x-0 top-16 z-40 origin-top overflow-hidden border-b border-line bg-void/98 backdrop-blur-xl transition-all duration-300 md:hidden ${
          open ? 'max-h-[80vh] opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        }`}
      >
        <ul className="container-page flex flex-col gap-1 py-6">
          <li>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                openSearch()
              }}
              className="block w-full rounded-xl px-4 py-3 text-left font-display text-xl text-mist"
            >
              <span aria-hidden>⌕</span> Search
            </button>
          </li>
          {[...NAV_LINKS, { label: 'Start Here', href: '/learn' }].map((l) => (
            <li key={l.href}>
              <NavLink
                to={l.href}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block rounded-xl px-4 py-3 font-display text-xl ${isActive ? 'text-energy' : 'text-mist'}`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}
